import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiUserPlus, FiTrash2, FiCheck, FiUsers } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { usersAPI, sharingAPI } from '../services/api';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 0;
  max-width: 550px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.8px;
`;

const FormInfo = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
`;

const FormTitle = styled.h4`
  font-size: 15px;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const FormDescription = styled.p`
  font-size: 13px;
  color: #075985;
  margin: 0;
  line-height: 1.4;
`;

const ShareForm = styled.div`
  display: flex;
  gap: 8px;
  align-items: stretch;
  flex-wrap: nowrap;
`;

const Select = styled.select`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e1;
  }
  
  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)' : props.variant === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' || props.variant === 'danger' ? 'white' : '#374151'};
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.variant === 'primary' ? '0 4px 12px 0 rgba(14, 165, 233, 0.4)' : 'none'};
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.variant === 'primary' ? '0 6px 16px 0 rgba(14, 165, 233, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SharedUsersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 240px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background: #f3f4f6;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const SharedUserItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
`;

const UserInfo = styled.div`
  flex: 1;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
`;

const UserEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const PermissionBadge = styled.span`
  padding: 5px 12px;
  background: ${props => {
    if (props.type === 'full') return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    if (props.type === 'edit') return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
    if (props.type === 'view_responses') return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
    return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
  }};
  color: white;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
`;

const RemoveButton = styled.button`
  background: #fee2e2;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: #fecaca;
    transform: scale(1.1);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 30px 20px;
  color: #9ca3af;
  font-size: 13px;
  background: #f9fafb;
  border-radius: 10px;
  border: 2px dashed #d1d5db;
`;

const UserShareModal = ({ isOpen, onClose, item, type = 'form' }) => {
  const [allUsers, setAllUsers] = useState([]);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permissionType, setPermissionType] = useState('full');
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (isOpen && item?.id) {
      loadUsers();
      loadSharedUsers();
    }
  }, [isOpen, item?.id]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setAllUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  };

  const loadSharedUsers = async () => {
    try {
      setLoading(true);
      const response = type === 'form' 
        ? await sharingAPI.getFormSharedUsers(item.id)
        : await sharingAPI.getFolderSharedUsers(item.id);
      setSharedUsers(response.data.data || []);
    } catch (error) {
      console.error('Error loading shared users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }

    try {
      setSharing(true);
      if (type === 'form') {
        await sharingAPI.shareForm(item.id, [selectedUserId], permissionType);
      } else {
        await sharingAPI.shareFolder(item.id, [selectedUserId], permissionType);
      }
      toast.success('Shared successfully');
      setSelectedUserId('');
      loadSharedUsers();
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleRemove = async (userId) => {
    if (!window.confirm('Remove access for this user?')) {
      return;
    }

    try {
      if (type === 'form') {
        await sharingAPI.removeFormAccess(item.id, userId);
      } else {
        await sharingAPI.removeFolderAccess(item.id, userId);
      }
      toast.success('Access removed');
      loadSharedUsers();
    } catch (error) {
      console.error('Error removing access:', error);
      toast.error('Failed to remove access');
    }
  };

  const getPermissionLabel = (permission) => {
    const labels = {
      full: 'Full Access',
      edit: 'Can Edit',
      view: 'Can View',
      view_responses: 'View Responses'
    };
    return labels[permission] || permission;
  };

  if (!isOpen || !item) return null;

  // Filter out users who already have access
  const availableUsers = allUsers.filter(user => 
    !sharedUsers.some(shared => shared.user_id === user.id)
  );

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            <FiUsers />
            Share {type === 'form' ? 'Form' : 'Folder'} with Users
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <FormInfo>
            <FormTitle>
              {type === 'form' ? '📝' : '📁'} {item.title || item.name || 'Untitled'}
            </FormTitle>
            <FormDescription>
              {item.description || 'No description provided'}
            </FormDescription>
          </FormInfo>

          <Section>
            <SectionTitle>👤 Add User</SectionTitle>
            <ShareForm>
              <Select 
                value={selectedUserId} 
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <option value="">Select a user...</option>
                {availableUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username} ({user.email})
                  </option>
                ))}
              </Select>

              <Select 
                value={permissionType} 
                onChange={(e) => setPermissionType(e.target.value)}
                style={{ maxWidth: '180px' }}
              >
                <option value="full">✨ Full Access</option>
                <option value="edit">✏️ Can Edit</option>
                {type === 'form' && <option value="view_responses">📊 View Responses</option>}
                <option value="view">👁️ View Only</option>
              </Select>

              <Button variant="primary" onClick={handleShare} disabled={sharing || !selectedUserId}>
                <FiUserPlus />
                {sharing ? 'Sharing...' : 'Share'}
              </Button>
            </ShareForm>
          </Section>

          <Section>
            <SectionTitle>
              👥 Shared With ({sharedUsers.length})
            </SectionTitle>
            {loading ? (
              <EmptyState>
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>⏳</div>
                Loading...
              </EmptyState>
            ) : sharedUsers.length === 0 ? (
              <EmptyState>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>👥</div>
                <strong style={{ fontSize: '14px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>
                  Not shared yet
                </strong>
                <small style={{ color: '#9ca3af', fontSize: '12px' }}>
                  Select a user above to start
                </small>
              </EmptyState>
            ) : (
              <SharedUsersList>
                {sharedUsers.map(sharedUser => (
                  <SharedUserItem key={sharedUser.user_id}>
                    <UserInfo>
                      <UserName>{sharedUser.username}</UserName>
                      <UserEmail>{sharedUser.email}</UserEmail>
                    </UserInfo>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <PermissionBadge type={sharedUser.permission_type}>
                        {getPermissionLabel(sharedUser.permission_type)}
                      </PermissionBadge>
                      <RemoveButton onClick={() => handleRemove(sharedUser.user_id)} title="Remove Access">
                        <FiTrash2 size={16} />
                      </RemoveButton>
                    </div>
                  </SharedUserItem>
                ))}
              </SharedUsersList>
            )}
          </Section>

          <div style={{ 
            padding: '10px 12px', 
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
            border: '2px solid #fbbf24',
            borderRadius: '10px',
            fontSize: '12px',
            color: '#78350f',
            lineHeight: '1.4',
            marginTop: '16px'
          }}>
            <strong style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '2px', fontSize: '12px' }}>
              <FiCheck /> Tip:
            </strong>
            Shared users see this {type} in their dashboard.
            {type === 'form' && ' Full = edit + responses.'}
          </div>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UserShareModal;

